import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import useSize from "../../../../Utils/useSize/useSize";
import Backend from "../../../../Backend/Backend";
import toast from "react-hot-toast";

const backend = await new Backend().init();

export default function Details() {
  const windowSize = useSize();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [symbol, setSymbol] = useState(null);
  const [symbolQty, setSymbolQty] = useState(null);

  // Load saved symbol data
  useEffect(() => {
    try {
      const savedSymbol =
        window.symboldata ||
        JSON.parse(localStorage.getItem("symboldata") || "null");

      if (savedSymbol) {
        setSymbol(savedSymbol);
        localStorage.setItem("symboldata", JSON.stringify(savedSymbol));
      } else {
        toast.error("No symbol data found.");
      }
    } catch (err) {
      console.error("Error loading symbol data:", err);
    }
  }, []);

  // Fetch symbol quantity details
  useEffect(() => {
    if (!symbol) return;
    fetchSymbolQty();
  }, [symbol]);

  const fetchSymbolQty = async () => {
    try {
      setLoading(true);
      const data = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        symbol_id: symbol?.symbol_id,
      };

      const res = await backend.symbol_qty(data);
      if (res?.error === "False") {
        setSymbolQty(res.symbol);
      } else {
        toast.error(res?.message || "Failed to load symbol details");
      }
    } catch (err) {
      console.error("Error fetching symbol details:", err);
      toast.error("Error fetching symbol details");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{ height: windowSize[1] - 100 }}
      className="bg-white text-black flex flex-col items-center p-4 font-sans"
    >
      {/* Header */}
      <div className="flex justify-between items-center w-full max-w-md mb-4">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full backdrop-blur-xl bg-white/60 flex items-center justify-center 
             shadow-[0_2px_10px_rgba(0,0,0,0.15)] 
             transition-transform duration-300 ease-in-out 
             hover:scale-105 active:scale-150"
        >
          <X className="text-black text-lg" />
        </button>
        <h2 className="text-lg font-semibold">
          {symbol?.symbol_display || "Symbol"}
        </h2>
        <div className="w-6" />
      </div>

      {/* Title */}
      {!loading && symbolQty ? (
        <>
          <h1 className="text-lg font-semibold mb-2 text-center">
            {symbolQty?.symbol}
          </h1>

          {/* Details */}
          <div style={{ overflowY: "auto" }} className="w-full max-w-md text-sm">
            {Object.entries({
              Category: symbolQty.category,
              Sector: symbolQty.sector,
              Industry: symbolQty.industry,
              Digits: symbolQty.digits,
              "Contract size": symbolQty.contractSize,
              Spread: symbolQty.spread,
              "Stops level": symbolQty.stopsLevel,
              "Margin currency": symbolQty.marginCurrency,
              "Profit currency": symbolQty.profitCurrency,
              Calculation: symbolQty.calculation,
              "Tick size": symbolQty.tickSize,
              "Tick value": symbolQty.tickValue,
              "Chart mode": symbolQty.chartMode,
              Trade: symbolQty.trade,
              Execution: symbolQty.execution,
              "GTC mode": symbolQty.gtcMode,
              Filling: symbolQty.filling,
              Expiration: symbolQty.expiration,
              Orders: symbolQty.orders,
            }).map(([label, value]) => (
              <InfoRow key={label} label={label} value={value} />
            ))}
          </div>
        </>
      ) : (
        <div className="mt-10 text-gray-500">
          {loading ? "Loading..." : "No data available"}
        </div>
      )}
    </div>
  );
}

// Row component for cleaner layout
function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between border-b border-gray-100 py-2">
      <span className="font-medium">{label}</span>
      <span className="text-gray-500 text-right">
        {value || "-"}
      </span>
    </div>
  );
}
